import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { CheckCircle2, Copy, Eye, FileText, Grid2X2, Plus, RotateCcw, Search, SlidersHorizontal, Table2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/Components/ui/Button';
import { Card, CardHead } from '@/Components/ui/Card';
import { Select } from '@/Components/ui/Select';
import { IconButton } from '@/Components/ui/IconButton';
import { PageHeader } from '@/Components/data/PageHeader';
import { EmptyState } from '@/Components/data/EmptyState';
import { FilterBar, FilterField } from '@/Components/data/FilterBar';
import { SearchField } from '@/Components/forms/SearchField';
import { CountBadge, StatusBadge } from '@/Components/ui/Badge';
import type { Tone } from '@/Components/ui/types';
import { TableActions } from '@/Components/ui/TableActions';
import { DataTableColumnsMenu } from '@/Components/data/DataTableColumnsMenu';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Components/ui/Dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/Components/ui/DropdownMenu';
import { Field } from '@/Components/forms/Field';
import { Input } from '@/Components/ui/Input';
import fichaTecnicaService, { type FichaTecnica } from '@/services/fichaTecnica';
import produtoService, { type Produto } from '@/services/produto';
import { getUuidCandidate } from '@/utils/uuid';

type ViewMode = 'table' | 'grid';
type FichaColumnId = 'produto' | 'versao' | 'status' | 'rendimento' | 'perda' | 'itens';

type FichaColumn = {
  id: FichaColumnId;
  label: string;
  render: (ficha: FichaTecnica) => ReactNode;
  primary?: boolean;
  hideOnMobile?: boolean;
  align?: 'left' | 'center' | 'right';
};

const produtoLabel = (produto: Produto) => `${produto.codigo || '-'} - ${produto.descricao || ''}`;

function getErrorMessage(error: unknown, fallback: string) {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { message?: string; error?: string } } }).response;
    return response?.data?.message || response?.data?.error || fallback;
  }

  return fallback;
}

const STATUS_LABELS: Record<string, string> = {
  RASCUNHO: 'Rascunho',
  APROVADA: 'Aprovada',
  OBSOLETA: 'Obsoleta',
};

const STATUS_TONES: Record<string, Tone> = {
  RASCUNHO: 'neutral',
  APROVADA: 'success',
  OBSOLETA: 'danger',
};

const columns: FichaColumn[] = [
  {
    id: 'produto',
    label: 'Produto',
    primary: true,
    render: (item) => (
      <div>
        <div>{item.produto_nome || item.produto_descricao || '-'}</div>
        <span className="nx-chip">{item.produto_codigo || '-'}</span>
      </div>
    ),
  },
  { id: 'versao', label: 'Versão', align: 'center', render: (item) => `v${item.versao}` },
  {
    id: 'status',
    label: 'Status',
    align: 'center',
    render: (item) => <StatusBadge label={STATUS_LABELS[item.status] || item.status} tone={STATUS_TONES[item.status] || 'neutral'} />,
  },
  { id: 'rendimento', label: 'Rendimento', align: 'right', render: (item) => Number(item.rendimento || 0).toLocaleString('pt-BR') },
  { id: 'perda', label: 'Perda padrão', align: 'right', hideOnMobile: true, render: (item) => `${Number(item.perda_padrao_percentual || 0).toLocaleString('pt-BR')}%` },
  { id: 'itens', label: 'Itens', align: 'right', hideOnMobile: true, render: (item) => item.itens?.length ?? 0 },
];

type ProductAutocompleteFilterProps = {
  produtos: Produto[];
  value: string;
  text: string;
  onTextChange: (value: string) => void;
  onSelect: (produto: Produto) => void;
  onClear: () => void;
};

function ProductAutocompleteFilter({ produtos, value, text, onTextChange, onSelect, onClear }: ProductAutocompleteFilterProps) {
  const [open, setOpen] = useState(false);
  const termo = text.trim().toLowerCase();
  const sugestoes = useMemo(() => {
    if (!termo) return produtos.slice(0, 12);
    return produtos
      .filter((produto) => produtoLabel(produto).toLowerCase().includes(termo))
      .slice(0, 12);
  }, [produtos, termo]);

  return (
    <div className="nx-autocomplete-filter">
      <div className="nx-search-field nx-search-field--filter nx-autocomplete-filter__control">
        <Search size={15} strokeWidth={2} aria-hidden />
        <input
          type="search"
          value={text}
          onChange={(event) => {
            onTextChange(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 140)}
          placeholder="Buscar produto..."
          aria-label="Buscar produto"
        />
        {text || value ? (
          <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={onClear} aria-label="Limpar produto">
            ×
          </button>
        ) : null}
      </div>
      {open ? (
        <div className="nx-autocomplete-filter__menu">
          {sugestoes.length === 0 ? (
            <div className="nx-autocomplete-filter__empty">Nenhum produto encontrado</div>
          ) : (
            sugestoes.map((produto) => (
              <button
                key={produto.id}
                type="button"
                className={getUuidCandidate(produto) === value ? 'is-selected' : undefined}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onSelect(produto);
                  setOpen(false);
                }}
              >
                <strong>{produto.codigo || '-'}</strong>
                <span>{produto.descricao}</span>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function FichaTecnicaPage() {
  const navigate = useNavigate();
  const [fichas, setFichas] = useState<FichaTecnica[]>([]);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [loading, setLoading] = useState(false);
  const [draftSearch, setDraftSearch] = useState('');
  const [search, setSearch] = useState('');
  const [draftProduto, setDraftProduto] = useState('');
  const [draftProdutoText, setDraftProdutoText] = useState('');
  const [produto, setProduto] = useState('');
  const [draftStatus, setDraftStatus] = useState('');
  const [status, setStatus] = useState('');
  const [perPage, setPerPage] = useState(20);
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [compactActions, setCompactActions] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<Record<FichaColumnId, boolean>>(
    () => Object.fromEntries(columns.map((column) => [column.id, true])) as Record<FichaColumnId, boolean>,
  );
  const [columnOrder, setColumnOrder] = useState<FichaColumnId[]>(() => columns.map((column) => column.id));
  const [dialogNovaVersao, setDialogNovaVersao] = useState(false);
  const [fichaParaNovaVersao, setFichaParaNovaVersao] = useState<FichaTecnica | null>(null);
  const [novaVersaoTexto, setNovaVersaoTexto] = useState('');
  const [erroOperacao, setErroOperacao] = useState('');

  const produtosValidos = useMemo(() => produtos.filter((item) => getUuidCandidate(item)), [produtos]);
  const activeColumns = useMemo(
    () => columnOrder
      .map((id) => columns.find((column) => column.id === id))
      .filter((column): column is FichaColumn => Boolean(column && visibleColumns[column.id])),
    [columnOrder, visibleColumns],
  );

  const carregar = async () => {
    setLoading(true);
    try {
      const data = await fichaTecnicaService.listar({
        produto: produto || undefined,
        status: status || undefined,
        search: search || undefined,
      });
      setFichas(data);
    } catch (error) {
      console.error('Erro ao carregar fichas técnicas', error);
      setFichas([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    produtoService.listar().then(setProdutos).catch(console.error);
  }, []);

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [produto, status, search]);

  const lastPage = Math.max(1, Math.ceil(fichas.length / perPage));
  const currentPage = Math.min(page, lastPage);
  const pageData = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return fichas.slice(start, start + perPage);
  }, [currentPage, fichas, perPage]);
  const from = fichas.length === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const to = fichas.length === 0 ? 0 : Math.min(from + pageData.length - 1, fichas.length);

  function submitFilters(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const produtoDigitado = draftProdutoText.trim();
    setPage(1);
    setSearch(draftSearch.trim() || (!draftProduto ? produtoDigitado : ''));
    setProduto(draftProduto);
    setStatus(draftStatus);
  }

  function clearFilters() {
    setDraftSearch('');
    setSearch('');
    setDraftProduto('');
    setDraftProdutoText('');
    setProduto('');
    setDraftStatus('');
    setStatus('');
    setPage(1);
  }

  const aprovar = async (item: FichaTecnica) => {
    setErroOperacao('');
    try {
      await fichaTecnicaService.aprovar(item.id);
      await carregar();
    } catch (error) {
      console.error('Erro ao aprovar ficha técnica', error);
      setErroOperacao(getErrorMessage(error, 'Não foi possível aprovar a ficha técnica.'));
    }
  };

  const abrirNovaVersao = (item: FichaTecnica) => {
    setFichaParaNovaVersao(item);
    setNovaVersaoTexto('');
    setDialogNovaVersao(true);
  };

  const confirmarNovaVersao = async () => {
    if (!fichaParaNovaVersao || !novaVersaoTexto) return;
    setErroOperacao('');
    try {
      await fichaTecnicaService.novaVersao(fichaParaNovaVersao.id, novaVersaoTexto);
      setDialogNovaVersao(false);
      await carregar();
    } catch (error) {
      console.error('Erro ao criar nova versão da ficha técnica', error);
      setErroOperacao(getErrorMessage(error, 'Não foi possível criar uma nova versão da ficha técnica.'));
    }
  };

  const deletar = async (item: FichaTecnica) => {
    setErroOperacao('');
    try {
      await fichaTecnicaService.excluir(item.id);
      await carregar();
    } catch (error) {
      console.error('Erro ao excluir ficha técnica', error);
      setErroOperacao(getErrorMessage(error, 'Não foi possível excluir a ficha técnica.'));
    }
  };

  const renderActions = (item: FichaTecnica) => (
    <div className="nx-table-actions-group nx-table-actions-group--buttons">
      <IconButton label="Visualizar" onClick={() => navigate(`/producao/fichas-tecnicas/${item.id}/visualizar`)}>
        <Eye size={15} strokeWidth={1.9} aria-hidden />
      </IconButton>
      <IconButton label="Aprovar Ficha" onClick={() => aprovar(item)} disabled={item.status !== 'RASCUNHO'}>
        <CheckCircle2 size={15} strokeWidth={1.9} aria-hidden />
      </IconButton>
      <IconButton label="Nova Versão" onClick={() => abrirNovaVersao(item)}>
        <Copy size={15} strokeWidth={1.9} aria-hidden />
      </IconButton>
      <TableActions
        onEdit={() => navigate(`/producao/fichas-tecnicas/${item.id}/editar`)}
        onDelete={() => deletar(item)}
        disabled={item.status === 'APROVADA'}
        deleteTitle="Excluir ficha técnica"
        deleteDescription={`Tem certeza de que deseja excluir a ficha técnica de "${item.produto_nome || item.produto_descricao}" (v${item.versao})? Esta ação não poderá ser desfeita.`}
      />
    </div>
  );

  return (
    <div className="nx-page">
      <PageHeader
        icon={<FileText size={21} strokeWidth={2} />}
        title="Fichas Técnicas"
        description="Cadastro de fichas técnicas e componentes de produção."
        actions={
          <Button type="button" onClick={() => navigate('/producao/fichas-tecnicas/novo')}>
            <Plus size={16} strokeWidth={2.2} />
            Nova Ficha
          </Button>
        }
      />

      <Card className="nx-page-card nx-sngpc-listing-card">
        {erroOperacao ? (
          <div className="nx-form-alert nx-form-alert--error" role="alert">
            <strong>Operação não realizada.</strong>
            <ul>
              <li>{erroOperacao}</li>
            </ul>
          </div>
        ) : null}

        <CardHead
          title="Listagem"
          actions={
            <div className="nx-card-head-actions nx-listing-toolbar">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="ghost">
                    <Grid2X2 size={16} />
                    Visualização
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Tipo de visualização</DropdownMenuLabel>
                  <DropdownMenuRadioGroup value={viewMode} onValueChange={(value) => setViewMode(value as ViewMode)}>
                    <DropdownMenuRadioItem value="table">
                      <Table2 size={15} /> Tabela
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="grid">
                      <Grid2X2 size={15} /> Grade
                    </DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>

              <DataTableColumnsMenu
                options={columns}
                columnOrder={columnOrder}
                visibility={visibleColumns}
                onToggle={(id, visible) => setVisibleColumns((current) => ({ ...current, [id]: visible }))}
                onReorder={(order) => setColumnOrder([...order])}
                onReset={() => {
                  setVisibleColumns(Object.fromEntries(columns.map((column) => [column.id, true])) as Record<FichaColumnId, boolean>);
                  setColumnOrder(columns.map((column) => column.id));
                }}
              />

              <Button type="button" variant="ghost" aria-pressed={compactActions} onClick={() => setCompactActions((value) => !value)}>
                <SlidersHorizontal size={15} />
                Modo de Ações
              </Button>

              <CountBadge count={`${fichas.length} registros`} />
            </div>
          }
        />

        <FilterBar
          onSubmit={submitFilters}
          search={
            <SearchField
              label="Buscar por produto ou versão"
              placeholder="Buscar produto ou versão..."
              value={draftSearch}
              onChange={(event) => setDraftSearch(event.target.value)}
            />
          }
          filters={
            <>
              <FilterField label="Produto" className="nx-sngpc-filter-field">
                <ProductAutocompleteFilter
                  produtos={produtosValidos}
                  value={draftProduto}
                  text={draftProdutoText}
                  onTextChange={(value) => {
                    setDraftProdutoText(value);
                    setDraftProduto('');
                  }}
                  onSelect={(selectedProduto) => {
                    setDraftProduto(getUuidCandidate(selectedProduto) || '');
                    setDraftProdutoText(produtoLabel(selectedProduto));
                  }}
                  onClear={() => {
                    setDraftProduto('');
                    setDraftProdutoText('');
                  }}
                />
              </FilterField>
              <FilterField label="Status" className="nx-sngpc-filter-field">
                <Select filter value={draftStatus} onChange={(event) => setDraftStatus(event.target.value)}>
                  <option value="">Todos</option>
                  <option value="RASCUNHO">Rascunho</option>
                  <option value="APROVADA">Aprovada</option>
                  <option value="OBSOLETA">Obsoleta</option>
                </Select>
              </FilterField>
              <FilterField label="Por página">
                <Select
                  filter
                  value={perPage}
                  onChange={(event) => {
                    setPage(1);
                    setPerPage(Number(event.target.value));
                  }}
                >
                  <option value={20}>20</option>
                  <option value={30}>30</option>
                  <option value={50}>50</option>
                </Select>
              </FilterField>
            </>
          }
          actions={
            <>
              <Button type="button" variant="ghost" onClick={clearFilters}>
                <RotateCcw size={15} aria-hidden />
                Limpar
              </Button>
              <Button type="submit">Filtrar</Button>
            </>
          }
        />

        {viewMode === 'table' ? (
          <div className="nx-table-wrap">
            <table className={`nx-table nx-sngpc-table ${compactActions ? 'nx-table--compact' : ''}`}>
              <thead>
                <tr>
                  {activeColumns.map((column) => (
                    <th key={column.id} className={`nx-cell-${column.align ?? 'left'} ${column.hideOnMobile ? 'nx-sngpc-hide-mobile' : ''}`}>
                      {column.label}
                    </th>
                  ))}
                  <th className="nx-table-actions">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={activeColumns.length + 1} className="nx-table-loading">Carregando registros...</td>
                  </tr>
                ) : pageData.length === 0 ? (
                  <tr>
                    <td colSpan={activeColumns.length + 1} className="nx-sngpc-empty-cell">Nenhuma ficha técnica encontrada.</td>
                  </tr>
                ) : (
                  pageData.map((item) => (
                    <tr key={item.id}>
                      {activeColumns.map((column) => (
                        <td key={column.id} className={`nx-cell-${column.align ?? 'left'} ${column.primary ? 'nx-table-name' : ''} ${column.hideOnMobile ? 'nx-sngpc-hide-mobile' : ''}`}>
                          {column.render(item)}
                        </td>
                      ))}
                      <td className="nx-table-actions">{renderActions(item)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : loading ? (
          <div className="nx-sngpc-grid-loading">Carregando registros...</div>
        ) : pageData.length === 0 ? (
          <EmptyState message="Nenhuma ficha técnica encontrada." />
        ) : (
          <div className="nx-sngpc-record-grid">
            {pageData.map((item) => (
              <article className="nx-sngpc-record" key={item.id}>
                {activeColumns.map((column) => (
                  <div key={column.id} className="nx-sngpc-record-field">
                    <span>{column.label}</span>
                    <strong>{column.render(item)}</strong>
                  </div>
                ))}
                <div className="nx-sngpc-record-actions">{renderActions(item)}</div>
              </article>
            ))}
          </div>
        )}

        <div className="nx-pagination">
          <span>{from}-{to} de {fichas.length}</span>
          <div className="nx-pagination-pages">
            <Button type="button" variant="ghost" disabled={currentPage <= 1} onClick={() => setPage((value) => value - 1)}>
              Anterior
            </Button>
            <Button type="button" variant="ghost" disabled={currentPage >= lastPage} onClick={() => setPage((value) => value + 1)}>
              Próxima
            </Button>
          </div>
        </div>
      </Card>

      <Dialog open={dialogNovaVersao} onOpenChange={setDialogNovaVersao}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova Versão</DialogTitle>
            <DialogDescription>
              Informe a nova versão para {fichaParaNovaVersao?.produto_nome || fichaParaNovaVersao?.produto_descricao}.
            </DialogDescription>
          </DialogHeader>
          <Field label="Versão *" htmlFor="nova-versao-texto">
            <Input id="nova-versao-texto" value={novaVersaoTexto} onChange={(event) => setNovaVersaoTexto(event.target.value)} placeholder="2.0" />
          </Field>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">Cancelar</Button>
            </DialogClose>
            <Button type="button" onClick={confirmarNovaVersao}>Criar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
